import type { KeyboardEvent } from 'react'
import type { StatusRole } from '@lighthouse/design'
import type { HealthCheckResponse } from '../../types/api'
import type { DeadLetterQueueStats } from '../../types/queues'
import type { SourceHealth, SourceHealthOverview, WorkerFleet, WorkerNode } from '../../types/observatory'
import { NOT_MEASURED, agoLabel, exactNumber } from '../../utils/format'
import { FREQUENT } from '../../utils/polling'
import { restartHeadline, slotsLabel } from '../workers/workerFormat'
import { groupByConnector } from '../sources/sourceFormat'
import { healthKpi, queryView, requestErrorText, sourcesLine } from '../overview/overviewFormat'
import type { HealthKpi, HealthLine, QueryLike } from '../overview/overviewFormat'

/**
 * The health board's pure rules: the Health Monitoring page (Beacons) and the Overview's System
 * Health panel both read the API's `/api/health`, and the page adds the worker fleet
 * (`api/observatory/workers`) and source reachability (`api/observatory/sources`), broken down per
 * host. Nothing here fetches; nothing here is a new measurement. Every function decides how a
 * SERVED reading is drawn, and the rule each keeps is the build plan's first: a thing nobody
 * observed renders NOT_MEASURED, never green.
 *
 * Pure and vitest-safe, like overviewFormat.ts: types, utils and sibling format modules only —
 * never services/api.ts (MSAL reads `window` at load) and never a hook.
 *
 * `healthRole`, `healthGroups`, `HEALTH_GROUPS` and `HealthGroup` were born in overviewFormat.ts
 * and moved here when a second surface needed them; overviewFormat.ts re-exports them, so its
 * importers did not move. That makes an import cycle between the two modules, and it is safe only
 * while neither CALLS the other at module load — both sides use the other's functions inside
 * function bodies alone. Keep it that way: a top-level constant computed from the other module
 * would read an uninitialised binding under whichever import order loads first.
 */

// ---------------------------------------------------------------------------
// Row mechanics
// ---------------------------------------------------------------------------

/**
 * A whole row that navigates, as a focusable element with the link role.
 *
 * Not a <button>: the row classes separate siblings with a CSS hairline on
 * `border-top`, and resetting a button's native border would erase it. Not an
 * <a href>: a bare anchor inside BrowserRouter is a full page reload, so every
 * in-app navigation here goes through useNavigate.
 *
 * Moved from components/overview/operations.tsx with HealthLineRow, its first user. It lives in
 * this .ts module rather than beside the row because a .tsx that exports a non-component breaks
 * React Fast Refresh (react-refresh/only-export-components); the Overview's Recent Executions rows
 * import it from here too.
 */
export function linkRowProps(onOpen: () => void, label: string) {
  return {
    role: 'link' as const,
    tabIndex: 0,
    'aria-label': label,
    onClick: onOpen,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onOpen()
      }
    },
    style: { cursor: 'pointer' },
  }
}

// ---------------------------------------------------------------------------
// /api/health: status names and groups (moved from overviewFormat.ts)
// ---------------------------------------------------------------------------

/** The API's health-check status name to a status role; a name this build does not know is a dash. */
export function healthRole(status: string | null | undefined): StatusRole | null {
  switch (status) {
    case 'Healthy': return 'good'
    case 'Degraded': return 'warning'
    case 'Unhealthy': return 'critical'
    default: return null
  }
}

const STATUS_RANK: Record<string, number> = { Healthy: 0, Degraded: 1, Unhealthy: 2 }

/** Worse first: Unhealthy over Degraded over Healthy, and a status this build does not know over all three. */
function rank(status: string): number {
  return STATUS_RANK[status] ?? 3
}

/**
 * How the API's check names fold into rows a reader recognises.
 *
 * The names are the registrations, verified in code (no host is started in this workflow):
 * - `self`: ServiceDefaults `AddDefaultHealthChecks`.
 * - `sqlserver` (ServiceDefaults, when a DataWarehouse connection string is configured) and
 *   `DataWarehouseSqlServerContext` (Aspire's `AddSqlServerDbContext`, named after the context type).
 * - `redis` (ServiceDefaults, when a `cache` connection string is configured) and
 *   `StackExchange.Redis` (Aspire's `AddRedisDistributedCache`).
 * - `RabbitMQ.Client` (Aspire's `AddRabbitMQClient`, locally) and `Azure.Messaging.ServiceBus`
 *   (Aspire's Service Bus client, in Azure — only when a health-check queue or topic is configured).
 * - `dead-letters`: the API's `DeadLetterHealthCheck`, Degraded above the configured depth.
 *
 * Aspire suffixes a KEYED client's check with `_<connection name>`, so a name also matches with
 * that suffix. Any other entry keeps its raw name — a check nobody mapped is still shown.
 *
 * `absentReason` is what the board says when an answer carries NO entry for a group: why this
 * instance may not have registered it, from the registration code (ServiceDefaults Extensions.cs
 * `AddDefaultHealthChecks`, Lighthouse.Api Program.cs). A missing group is never dropped — a row
 * that vanishes reads as a row that is fine.
 */
export const HEALTH_GROUPS: readonly {
  key: string
  label: string
  names: readonly string[]
  absentReason: string
}[] = [
  {
    key: 'api',
    label: 'API process',
    names: ['self'],
    absentReason: 'Registered unconditionally by the service defaults, so an answer without it did not come from that registration.',
  },
  {
    key: 'warehouse',
    label: 'Data Warehouse',
    names: ['sqlserver', 'DataWarehouseSqlServerContext'],
    absentReason: 'Registered only when the API has a DataWarehouse connection string.',
  },
  {
    key: 'cache',
    label: 'Cache (Redis)',
    names: ['redis', 'StackExchange.Redis'],
    absentReason: 'Registered only when a cache connection string is configured.',
  },
  {
    key: 'broker',
    label: 'Message broker',
    names: ['RabbitMQ.Client', 'Azure.Messaging.ServiceBus'],
    absentReason: 'Locally the RabbitMQ client registers a check; the Azure Service Bus client registers one only '
      + 'when a health-check queue or topic is configured, and none is.',
  },
  {
    key: 'dead-letters',
    label: 'Dead-letter queues',
    names: ['dead-letters'],
    absentReason: 'Registered in every environment except Testing.',
  },
]

function groupFor(checkName: string) {
  return HEALTH_GROUPS.find((g) => g.names.some((n) => checkName === n || checkName.startsWith(`${n}_`)))
}

/** One System Health row built from one or more health-check entries. */
export interface HealthGroup {
  key: string
  label: string
  /** The worst status among the group's entries, as served. */
  status: string
  role: StatusRole | null
  /** The raw check names folded into this row, in served order. */
  checks: string[]
  /** The entries' descriptions, de-duplicated; null when none said anything. */
  detail: string | null
}

/**
 * The served entries folded into rows: the known groups in a fixed order, then every unmapped
 * entry under its own name in served order. A group takes its WORST entry's status, so a healthy
 * `sqlserver` cannot hide an unhealthy `DataWarehouseSqlServerContext`.
 */
export function healthGroups(report: HealthCheckResponse): HealthGroup[] {
  const known = new Map<string, HealthGroup>()
  const unmapped: HealthGroup[] = []
  const descriptions = new Map<HealthGroup, string[]>()

  for (const [name, entry] of Object.entries(report.entries ?? {})) {
    const group = groupFor(name)
    let row: HealthGroup | undefined
    if (group) {
      row = known.get(group.key)
      if (!row) {
        row = { key: group.key, label: group.label, status: entry.status, role: null, checks: [], detail: null }
        known.set(group.key, row)
      }
    } else {
      row = { key: `check:${name}`, label: name, status: entry.status, role: null, checks: [], detail: null }
      unmapped.push(row)
    }
    row.checks.push(name)
    if (rank(entry.status) > rank(row.status)) row.status = entry.status
    const said = entry.description?.trim()
    if (said) {
      const list = descriptions.get(row) ?? []
      if (!list.includes(said)) list.push(said)
      descriptions.set(row, list)
    }
  }

  const ordered = [
    ...HEALTH_GROUPS.map((g) => known.get(g.key)).filter((g): g is HealthGroup => g !== undefined),
    ...unmapped,
  ]
  for (const row of ordered) {
    row.role = healthRole(row.status)
    const list = descriptions.get(row)
    row.detail = list && list.length > 0 ? list.join(' · ') : null
  }
  return ordered
}

// ---------------------------------------------------------------------------
// /api/health: the board's rows
// ---------------------------------------------------------------------------

/**
 * A served .NET `TimeSpan` ("c" format: `[-][d.]hh:mm:ss[.fffffff]`) in milliseconds, or null when
 * it is not one. The API writes every duration with `TimeSpan.ToString()`.
 */
export function checkDurationMs(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null
  const m = /^(-)?(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?$/.exec(value.trim())
  if (!m) return null
  const [, negative, days, hours, minutes, seconds, fraction] = m
  if (Number(minutes) > 59 || Number(seconds) > 59 || Number(hours) > 23) return null
  const whole = (Number(days ?? 0) * 86400) + (Number(hours) * 3600) + (Number(minutes) * 60) + Number(seconds)
  const ms = whole * 1000 + (fraction ? Number(`0.${fraction}`) * 1000 : 0)
  return negative ? null : ms
}

/**
 * "12 ms", "1.4 s", "2m 5s" — how long a check took, as served. Null for anything that is not a
 * TimeSpan, never "0 ms": a duration nobody could read is not a fast check.
 *
 * Under a millisecond reads "<1 ms", including an exact zero: the check finished inside a timer
 * tick, which is a measurement, and "0 ms" would read as a check that did not run.
 */
export function checkDurationText(value: string | null | undefined): string | null {
  const ms = checkDurationMs(value)
  if (ms == null) return null
  if (ms < 1) return '<1 ms'
  if (ms < 1000) return `${Math.round(ms)} ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${Math.round(seconds * 10) / 10} s`
  const whole = Math.round(seconds)
  const mm = Math.floor(whole / 60)
  const ss = whole % 60
  if (mm < 60) return ss ? `${mm}m ${ss}s` : `${mm}m`
  return `${Math.floor(mm / 60)}h ${mm % 60}m`
}

/** The caption a group with no entry gets. */
export const NOT_CHECKED_BY_THIS_INSTANCE = 'Not checked by this instance'

/** One row of the API's checks, as the board draws it. */
export interface ApiCheckRow {
  key: string
  label: string
  /** The served status name — the group's worst entry. Null when the answer had no entry for it. */
  served: string | null
  /** The badge. Null is a dash with `statusLabel` beside it. */
  role: StatusRole | null
  /** The badge's text, or the dash's caption. */
  statusLabel: string
  /** The raw check names folded into this row, in served order. Empty for a group nobody checked. */
  checks: string[]
  /** What the checks said, or why nothing did. */
  detail: string | null
  /** The slowest entry's duration, via {@link checkDurationText}. Null when none was readable. */
  slowest: string | null
  /** An in-app path the row opens, when it has one. */
  to: string | null
}

function slowestOf(report: HealthCheckResponse, checks: readonly string[]): string | null {
  let worst: { ms: number; raw: string } | null = null
  for (const name of checks) {
    const raw = report.entries?.[name]?.duration
    const ms = checkDurationMs(raw)
    if (ms != null && (worst == null || ms > worst.ms)) worst = { ms, raw: raw! }
  }
  return worst ? checkDurationText(worst.raw) : null
}

/**
 * One row per known group, in {@link HEALTH_GROUPS} order, then every unmapped check under its raw
 * name in served order.
 *
 * A group with NO entry is KEPT, as a dash captioned "Not checked by this instance" with the
 * reason it may be missing. Dropping it — what healthGroups alone does — is how an Azure answer
 * with no broker check drew as five fine rows instead of four fine rows and a hole.
 */
export function apiCheckRows(report: HealthCheckResponse): ApiCheckRow[] {
  const served = healthGroups(report)
  const toRow = (g: HealthGroup): ApiCheckRow => ({
    key: g.key,
    label: g.label,
    served: g.status,
    role: healthRole(g.status),
    statusLabel: g.status || NOT_MEASURED,
    checks: [...g.checks],
    detail: g.detail,
    slowest: slowestOf(report, g.checks),
    to: null,
  })

  const known = HEALTH_GROUPS.map((group): ApiCheckRow => {
    const found = served.find((g) => g.key === group.key)
    if (found) return toRow(found)
    return {
      key: group.key,
      label: group.label,
      served: null,
      role: null,
      statusLabel: NOT_CHECKED_BY_THIS_INSTANCE,
      checks: [],
      detail: group.absentReason,
      slowest: null,
      to: null,
    }
  })
  const unmapped = served.filter((g) => !HEALTH_GROUPS.some((known) => known.key === g.key)).map(toRow)
  return [...known, ...unmapped]
}

/** The slice of the dead-letter stats query this module reads. */
export interface DeadLetterStatsQuery extends QueryLike<DeadLetterQueueStats[]> {
  dataUpdatedAt: number
}

/** A React Query `dataUpdatedAt` (epoch ms, 0 before any answer) as agoLabel's "12m ago", or a dash. */
export function fetchedAgo(dataUpdatedAt: number): string {
  return dataUpdatedAt > 0 ? agoLabel(new Date(dataUpdatedAt).toISOString()) : NOT_MEASURED
}

/**
 * The `dead-letters` row, checked against the per-queue reading.
 *
 * `DeadLetterHealthCheck` sums `MessageCount`, and a queue the monitor could not inspect reports 0
 * with `Unavailable` set — so the check can read Healthy over queues nobody looked into. The
 * per-queue stats (`api/system/dead-letters`, the rail badge's query) carry that flag:
 *
 * - Any MEASURED unavailable queue under a Healthy check makes the row a dash, "not all inspected",
 *   and it opens /queues. A Degraded or Unhealthy check keeps its colour — uninspected queues can
 *   only add messages, so a depth already over the threshold stays true — and gains the same
 *   sentence.
 * - A FAILED per-queue reading leaves a Healthy check unproven exactly when inspection cannot be
 *   read, so it is a dash too, "inspection unknown", opening /queues. A Degraded or Unhealthy check
 *   keeps its colour, for the same reason as above.
 * - While the stats are still loading the served status stands and the detail says the inspection
 *   has not been read yet — so the row does not flicker on every mount.
 * - Any other row, and a dead-letters row the answer had no entry for, is returned unchanged.
 */
export function deadLetterIntegrity(row: ApiCheckRow, stats: DeadLetterStatsQuery): ApiCheckRow {
  if (row.key !== 'dead-letters' || row.served == null) return row
  const append = (sentence: string) => (row.detail ? `${row.detail} · ${sentence}` : sentence)

  const view = queryView(stats)
  if (view === 'error') {
    const sentence = `Whether every queue was inspected could not be read (${requestErrorText('the per-queue reading', stats.error).replace(/\.$/, '')}), `
      + `so this check's '${row.served}' is not confirmed.`
    if (row.role !== 'good') return { ...row, detail: append(sentence), to: '/queues' }
    return { ...row, role: null, statusLabel: 'inspection unknown', detail: append(sentence), to: '/queues' }
  }
  if (view === 'loading') return { ...row, detail: append('Whether every queue was inspected has not been read yet.') }

  const queues = stats.data!
  const unavailable = queues.filter((q) => q.unavailable).length
  if (unavailable === 0) return row

  const sentence = `${exactNumber(unavailable)} of ${exactNumber(queues.length)} dead-letter queues could not be inspected. `
    + `This check counts them as empty, so its '${row.served}' does not cover them. `
    + `Per-queue reading ${fetchedAgo(stats.dataUpdatedAt)}.`
  if (row.role !== 'good') return { ...row, detail: append(sentence), to: '/queues' }
  return { ...row, role: null, statusLabel: 'not all inspected', detail: append(sentence), to: '/queues' }
}

/**
 * What the served aggregate does not cover, read from the same rows the board draws: a known group
 * the answer carries no entry for ("Not checked by this instance"), a check downgraded by
 * {@link deadLetterIntegrity} (queues not all inspected, or inspection unknown), and an entry whose
 * status name this build does not know. Empty when the rows cover everything the board knows of.
 */
export function checkGaps(report: HealthCheckResponse, deadLetters?: DeadLetterStatsQuery): string[] {
  const rows = apiCheckRows(report).map((r) => (deadLetters ? deadLetterIntegrity(r, deadLetters) : r))
  const gaps: string[] = []
  const unchecked = rows.filter((r) => r.served == null).length
  if (unchecked > 0) gaps.push(`${exactNumber(unchecked)} ${unchecked === 1 ? 'group' : 'groups'} not checked by this instance`)
  for (const r of rows) {
    if (r.served != null && r.role == null && healthRole(r.served) != null) gaps.push(`${r.label.toLowerCase()} ${r.statusLabel}`)
  }
  const unknown = rows.filter((r) => r.served != null && healthRole(r.served) == null).length
  if (unknown > 0) gaps.push(`${exactNumber(unknown)} ${unknown === 1 ? 'status' : 'statuses'} this build does not know`)
  return gaps
}

/**
 * A KPI tile built on the served aggregate, held to what the rows beneath it show. The aggregate
 * only speaks for the checks that ran, so any gap drops the good tone — the served word stays, in
 * no colour — and the sublabel says what is not covered. A warning or critical aggregate keeps its
 * colour: a gap can only hide more trouble, never less.
 */
function heldToRows(tile: HealthKpi, report: HealthCheckResponse, deadLetters: DeadLetterStatsQuery | undefined, base: string): HealthKpi {
  const gaps = checkGaps(report, deadLetters)
  if (gaps.length === 0) return { ...tile, sublabel: base }
  return {
    ...tile,
    status: tile.status === 'good' ? undefined : tile.status,
    sublabel: `${base} · ${gaps.join(' · ')}`,
  }
}

function healthyCount(report: HealthCheckResponse): string {
  const entries = Object.values(report.entries ?? {})
  if (entries.length === 0) return 'the API reported no checks'
  return `${entries.filter((e) => e.status === 'Healthy').length} of ${entries.length} checks healthy`
}

/**
 * The summary band's "API checks" tile: the KPI tile's served aggregate, with a sublabel that
 * names whose view it is — the one API instance that answered — and never green over a gap the
 * rows below it show. No percentage.
 */
export function apiChecksTile(q: QueryLike<HealthCheckResponse>, deadLetters?: DeadLetterStatsQuery): HealthKpi {
  const tile = healthKpi(q)
  if (queryView(q) !== 'ready') return tile
  const report = q.data!
  const count = healthyCount(report)
  const base = Object.keys(report.entries ?? {}).length === 0 ? count : `${count}, as seen by the API instance that answered`
  return heldToRows(tile, report, deadLetters, base)
}

/**
 * The Overview's System Health KPI: {@link healthKpi} held to the same rows as the System Health
 * panel beside it, so the tile cannot read green over a row the panel dashes.
 */
export function systemHealthKpi(q: QueryLike<HealthCheckResponse>, deadLetters?: DeadLetterStatsQuery): HealthKpi {
  const tile = healthKpi(q)
  if (queryView(q) !== 'ready') return tile
  return heldToRows(tile, q.data!, deadLetters, tile.sublabel)
}

/** The API section's footer: when the checks ran, how often they rerun, and how long they took. */
export function apiFooterText(report: HealthCheckResponse, dataUpdatedAt: number): string {
  const total = checkDurationText(report.totalDuration) ?? NOT_MEASURED
  return `Checks ran when this page asked, ${fetchedAgo(dataUpdatedAt)}; they rerun every `
    + `${Math.round(FREQUENT / 1000)} s. Total ${total}.`
}

// ---------------------------------------------------------------------------
// Summary tiles for the two projections
// ---------------------------------------------------------------------------

/** A summary-band tile. `status` absent is a dash — no colour for a non-answer. */
export interface SummaryTile {
  value: string
  status: StatusRole | undefined
  sublabel: string
}

/**
 * A projection's line (workersLine, sourcesLine) as a tile: a status is the label with its badge;
 * a null status is a dash, captioned with the line's own label and detail. Loading and a failed
 * request are dashes that say which they are.
 */
export function lineTile<T>(q: QueryLike<T>, what: string, line: (data: T) => HealthLine): SummaryTile {
  const view = queryView(q)
  if (view === 'error') return { value: NOT_MEASURED, status: undefined, sublabel: requestErrorText(what, q.error) }
  if (view === 'loading') return { value: NOT_MEASURED, status: undefined, sublabel: `asking for ${what}…` }
  const read = line(q.data!)
  if (read.status == null) {
    return { value: NOT_MEASURED, status: undefined, sublabel: read.detail ? `${read.label} — ${read.detail}` : read.label }
  }
  return { value: read.label, status: read.status, sublabel: read.detail ?? '' }
}

// ---------------------------------------------------------------------------
// Hosts
// ---------------------------------------------------------------------------

/**
 * One worker process's line on its host card.
 *
 * First match wins:
 * 1. A stale projection withholds every worker's status — a dash, "stale". The server serves each
 *    node of a stale projection as critical "Not responding", which may be the publisher, not the
 *    worker.
 * 2. Not live: the served critical "Not responding", and when it last beat.
 * 3. Live with no slots: a warning, "No slots configured". The server serves good/"Idle" because
 *    its utilization divides by zero to zero; a process that can accept no work is not idle. (The
 *    Workers page overrides it the same way.)
 * 4. Otherwise the served status and label, with its slots.
 *
 * The restart ledger's headline is appended whenever the row stands for older incarnations.
 */
export function workerNodeLine(worker: WorkerNode, fleet: Pick<WorkerFleet, 'isProjectionStale'>): HealthLine {
  const restart = restartHeadline(worker.supersededIncarnationCount, worker.previousIncarnationLastSeenAt)
  const withRestart = (detail: string) => (restart ? `${detail} · ${restart}` : detail)

  if (fleet.isProjectionStale) {
    return { status: null, label: 'stale', detail: withRestart(`last heartbeat ${agoLabel(worker.lastHeartbeatAt)}, as last published`) }
  }
  if (!worker.isLive) {
    return { status: worker.status, label: worker.statusLabel, detail: withRestart(`last heartbeat ${agoLabel(worker.lastHeartbeatAt)}`) }
  }
  if (worker.workerCount === 0) {
    return {
      status: 'warning',
      label: 'No slots configured',
      detail: withRestart('Beating, but reports zero worker slots, so it can accept no work.'),
    }
  }
  return { status: worker.status, label: worker.statusLabel, detail: withRestart(slotsLabel(worker.busyWorkerCount, worker.workerCount)) }
}

/** A notice above the host cards about a feed as a whole. */
export interface HostBanner {
  key: string
  feed: 'workers' | 'sources'
  kind: 'never-reported' | 'stale' | 'failed' | 'loading'
  text: string
}

/** One worker process on a host card. */
export interface HostWorkerRow {
  key: string
  name: string
  line: HealthLine
}

/**
 * One machine. Deliberately NO status: a host is not "healthy" because its worker beats and not
 * "down" because one connector cannot reach Oracle — the rows say what each feed measured.
 */
export interface HostCard {
  /** The lowercased host name, or `worker:<workerId>` for a worker that reported none. */
  key: string
  /** The machine-name spelling (a source row's) when there is one, otherwise the worker's. */
  hostName: string
  workers: HostWorkerRow[]
  sources: SourceHealth[]
  /** Neutral lines: a feed that names nothing under this host, or a worker with no host name. */
  notes: string[]
}

export interface HostBoard {
  banners: HostBanner[]
  cards: HostCard[]
  /** Both feeds still loading: draw skeletons, say nothing. */
  loading: boolean
  /** No host to draw, and why — never an all-clear. Null when there are cards, or while loading. */
  empty: { title: string; message: string } | null
}

/** A sentence set inside parentheses, without the full stop that would double the one after them. */
function inParens(sentence: string): string {
  return sentence.replace(/\.$/, '')
}

export const NO_WORKER_UNDER_HOST = 'No worker process is reported under this name.'
export const NO_SOURCE_UNDER_HOST = 'No source reading is filed under this name. The host may register no source '
  + 'probe, or report under a different spelling of its name.'

/**
 * The per-host board: one card per machine name found in either feed, matched ignoring case.
 *
 * - Worker host names are Hangfire's server-name prefix, lowercased; source host names are
 *   `Environment.MachineName`, case kept. The key is the name lowercased — the server matches
 *   OrdinalIgnoreCase — and the card shows the machine-name spelling when a source row has one.
 * - A worker with no host name gets its own card, keyed by its worker id: pooling every nameless
 *   worker under "" would make one machine out of several.
 * - Order: hosts as the worker feed serves them, then source-only hosts as the source feed serves
 *   them. Nothing is sorted.
 * - A card present in one feed only says so neutrally — but only when the OTHER feed answered:
 *   "no worker under this name" is a claim about the fleet, and a fleet that did not come back
 *   supports no claim.
 * - Banners, per feed: never reported (checked FIRST — that envelope is not stale), stale, failed,
 *   or still loading.
 */
export function hostCards(
  fleetQuery: QueryLike<WorkerFleet>,
  sourcesQuery: QueryLike<SourceHealthOverview>,
): HostBoard {
  const fleetView = queryView(fleetQuery)
  const sourceView = queryView(sourcesQuery)
  const fleet = fleetView === 'ready' ? fleetQuery.data! : null
  const sources = sourceView === 'ready' ? sourcesQuery.data! : null

  const banners: HostBanner[] = []
  if (fleet?.hasNeverReported) {
    banners.push({ key: 'workers-never', feed: 'workers', kind: 'never-reported', text: 'No worker has published a heartbeat to this database.' })
  } else if (fleet?.isProjectionStale) {
    banners.push({
      key: 'workers-stale',
      feed: 'workers',
      kind: 'stale',
      text: `Automaton last published worker heartbeats ${agoLabel(fleet.observedAt)}. Worker statuses below are `
        + 'withheld: this may be the publisher, not the workers.',
    })
  } else if (fleetView === 'error') {
    banners.push({
      key: 'workers-failed',
      feed: 'workers',
      kind: 'failed',
      text: `The worker fleet could not be read (${inParens(requestErrorText('the worker fleet', fleetQuery.error))}). `
        + 'Hosts known only from it are not listed.',
    })
  } else if (fleetView === 'loading') {
    banners.push({ key: 'workers-loading', feed: 'workers', kind: 'loading', text: 'Still reading the worker fleet; hosts known only from it are not listed yet.' })
  }

  if (sources?.hasNeverReported || sources?.isProjectionStale) {
    // sourcesLine's own wording, so the Overview row and this banner cannot drift apart.
    banners.push({
      key: sources.hasNeverReported ? 'sources-never' : 'sources-stale',
      feed: 'sources',
      kind: sources.hasNeverReported ? 'never-reported' : 'stale',
      text: sourcesLine(sources).detail ?? '',
    })
  } else if (sourceView === 'error') {
    banners.push({
      key: 'sources-failed',
      feed: 'sources',
      kind: 'failed',
      text: `The source readings could not be read (${inParens(requestErrorText('source health', sourcesQuery.error))}). `
        + 'Hosts known only from it are not listed.',
    })
  } else if (sourceView === 'loading') {
    banners.push({ key: 'sources-loading', feed: 'sources', kind: 'loading', text: 'Still reading the source readings; hosts known only from them are not listed yet.' })
  }

  const cards: HostCard[] = []
  const byKey = new Map<string, HostCard>()

  for (const worker of fleet?.workers ?? []) {
    const name = worker.hostName?.trim() ?? ''
    const row: HostWorkerRow = { key: worker.workerId, name: worker.name, line: workerNodeLine(worker, fleet!) }
    if (!name) {
      cards.push({
        key: `worker:${worker.workerId}`,
        hostName: worker.name || worker.workerId,
        workers: [row],
        sources: [],
        notes: ['This worker reported no host name, so it is matched to no source reading.'],
      })
      continue
    }
    const key = name.toLowerCase()
    let card = byKey.get(key)
    if (!card) {
      card = { key, hostName: name, workers: [], sources: [], notes: [] }
      byKey.set(key, card)
      cards.push(card)
    }
    card.workers.push(row)
  }

  for (const source of sources?.sources ?? []) {
    const name = source.hostName?.trim() ?? ''
    const key = name.toLowerCase()
    let card = byKey.get(key)
    if (!card) {
      card = { key, hostName: name || NOT_MEASURED, workers: [], sources: [], notes: [] }
      byKey.set(key, card)
      cards.push(card)
    } else if (card.sources.length === 0 && name) {
      // The machine-name spelling wins over Hangfire's lowercased prefix.
      card.hostName = name
    }
    card.sources.push(source)
  }

  for (const card of cards) {
    if (card.key.startsWith('worker:')) continue
    if (card.workers.length === 0 && fleet) card.notes.push(NO_WORKER_UNDER_HOST)
    if (card.sources.length === 0 && sources) card.notes.push(NO_SOURCE_UNDER_HOST)
  }

  const loading = fleetView === 'loading' && sourceView === 'loading'
  let empty: HostBoard['empty'] = null
  if (!loading && cards.length === 0) {
    const silent = (view: string, data: { hasNeverReported: boolean } | null) => view === 'error' || Boolean(data?.hasNeverReported)
    const message = banners.map((b) => b.text).join(' ')
    if (fleetView === 'error' && sourceView === 'error') {
      empty = { title: 'No Automaton host could be read', message }
    } else if (silent(fleetView, fleet) && silent(sourceView, sources)) {
      empty = { title: 'No Automaton host has reported', message }
    } else if (fleetView !== 'loading' && sourceView !== 'loading') {
      empty = { title: 'No host is listed', message: message || 'Neither the worker heartbeat nor the source readings name a machine.' }
    }
  }

  return { banners, cards, loading, empty }
}

// ---------------------------------------------------------------------------
// By source, across hosts
// ---------------------------------------------------------------------------

/** One host's chip on a connector line. */
export interface ConnectorChip {
  key: string
  hostName: string
  status: StatusRole | null
  statusLabel: string
}

export interface ConnectorLine {
  connectorId: string
  displayName: string
  chips: ConnectorChip[]
}

/**
 * The source list read across hosts: one line per connector, one chip per host, in served order
 * (groupByConnector keeps the server's adjacency on purpose). No ratio — "2 of 3 hosts" is a
 * count of readings, not of reachability, and a dash in it is not a failure.
 */
export function connectorLines(overview: SourceHealthOverview): ConnectorLine[] {
  return groupByConnector(overview.sources).map((group) => ({
    connectorId: group.connectorId,
    displayName: group.rows[0]?.displayName || group.connectorId,
    chips: group.rows.map((row) => ({
      key: `${row.connectorId}@${row.hostName}`,
      hostName: row.hostName,
      status: row.status ?? null,
      statusLabel: row.statusLabel,
    })),
  }))
}

// ---------------------------------------------------------------------------
// What nothing checks
// ---------------------------------------------------------------------------

/** A thing Observatory can name that nothing reports on. Each is a dash, "Not checked". */
export interface NotCheckedRow {
  key: string
  name: string
  reason: string
}

export const NOT_CHECKED_ROWS: readonly NotCheckedRow[] = [
  {
    key: 'automaton-health',
    name: "Automaton's own health checks",
    reason: 'Each Automaton answers /health on its own host, including its outbox check when the outbox drains '
      + 'there, but nothing projects that answer where the API can serve it.',
  },
  {
    key: 'hangfire-dashboard',
    name: 'Hangfire Dashboard',
    reason: 'It has no warehouse connection, so it publishes nothing Observatory can read.',
  },
  {
    key: 'scheduler',
    name: 'Scheduler (recurring jobs)',
    reason: 'Nothing records whether a recurring job fired on time.',
  },
  {
    key: 'notifications',
    name: 'Notification Service',
    reason: 'Nothing checks the email function or records delivery results.',
  },
  {
    key: 'storage',
    name: 'File Storage (Strongbox)',
    reason: 'The API registers no health check for blob storage.',
  },
]
